/**
 * ProfileAccount.jsx — subscription card + open inbox + export +
 * reading history + space toggle + Vault request. Catch-all "everything
 * else" sub-page from the Feb 26 2026 Profile restructure.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings as SettingsIcon, Mail, Calendar, Crown, ArrowRight } from 'lucide-react';
import ProfileSubPage from '../components/ProfileSubPage';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import SpaceToggle from '../components/SpaceToggle';
import RequestVaultButton from '../components/RequestVaultButton';
import QuizHistory from '../components/QuizHistory';
import ProfileCoursesCard from '../components/ProfileCoursesCard';
import RetentionQuizToggleCard from '../components/RetentionQuizToggleCard';
import { checkAuth, authFetch } from '../utils/authUtils';
import { getActiveSpace, onSpaceChange } from '../utils/spaceContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ProfileAccount = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscriptionError, setSubscriptionError] = useState('');
  const [cancelState, setCancelState] = useState('idle');
  const [space, setSpace] = useState(getActiveSpace());

  useEffect(() => onSpaceChange((s) => setSpace(s)), []);

  const loadAccount = useCallback(async () => {
    setLoading(true);
    setSubscriptionError('');
    try {
      const u = await checkAuth();
      if (!u) { navigate('/login'); return; }
      setUser(u);
      const sr = await authFetch(`${BACKEND_URL}/api/subscription/status`);
      if (!sr.ok) throw new Error(`HTTP ${sr.status}`);
      setSubscription(await sr.json());
    } catch (_) {
      setSubscription(null);
      setSubscriptionError('We could not load your membership status.');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { loadAccount(); }, [loadAccount]);

  const isPremium = subscription?.subscription_tier === 'premium';
  const isWriter = user?.role === 'writer' || user?.role === 'admin';
  const showWriterStats = isWriter && space === 'writer';

  const handleCancelSubscription = async () => {
    if (!window.confirm('Cancel your subscription? It stays active until the end of your billing period.')) return;
    setCancelState('saving');
    try {
      const response = await authFetch(`${BACKEND_URL}/api/subscription/cancel`, { method: 'POST' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      setCancelState('saved');
      await loadAccount();
    } catch (_) {
      setCancelState('error');
    }
  };

  return (
    <ProfileSubPage title="Account Settings" icon={SettingsIcon} accent="slate">
      {isWriter && <SpaceToggle />}
      {loading && <p className="text-sm text-slate-400" role="status">Loading account settings…</p>}

      <RequestVaultButton variant="profile" />

      {/* Subscription */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Crown className="w-5 h-5 text-yellow-400" /> Subscription
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {subscriptionError ? (
            <div className="rounded-xl border border-amber-400/30 bg-amber-500/10 p-4 text-sm text-amber-100" role="status" data-testid="subscription-unavailable">
              <p>{subscriptionError}</p>
              <Button type="button" onClick={loadAccount} variant="outline" className="mt-3 border-amber-400/40 text-amber-100 hover:bg-amber-400/10">Retry</Button>
            </div>
          ) : isPremium ? (
            <>
              <div className="p-4 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 rounded-lg">
                <p className="text-yellow-400 font-semibold mb-1">Premium Member</p>
                {subscription?.expires_at ? (
                  <p className="text-sm text-gray-300">Expires: {new Date(subscription.expires_at).toLocaleDateString()}</p>
                ) : <p className="text-sm text-gray-300">Renewal date unavailable</p>}
              </div>
              <Button onClick={handleCancelSubscription} disabled={cancelState === 'saving'} variant="outline" className="w-full border-red-600 text-red-400 hover:bg-red-900/20 disabled:opacity-60">
                {cancelState === 'saving' ? 'Cancelling…' : 'Cancel Subscription'}
              </Button>
              {cancelState === 'saved' && <p className="text-xs text-emerald-300" role="status">Cancellation request saved.</p>}
              {cancelState === 'error' && <p className="text-xs text-rose-300" role="alert">We could not cancel your subscription. Please try again.</p>}
            </>
          ) : subscription ? (
            <>
              <div className="p-4 bg-slate-700/30 rounded-lg">
                <p className="text-white font-semibold mb-1">No active Premium membership</p>
                <p className="text-sm text-gray-400">Plan details are available on the plans page.</p>
              </div>
              <Button
                onClick={() => navigate('/premium')}
                className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white font-semibold"
              >
                <Crown className="w-4 h-4 mr-2" /> View plans <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </>
          ) : null}
        </CardContent>
      </Card>

      {/* Courses */}
      <ProfileCoursesCard variant={showWriterStats ? 'writer' : 'reader'} />

      {/* Quiz History (reader only) */}
      {!showWriterStats && <QuizHistory />}

      {/* Feb 12 2026 — Optional retention quiz toggle (Book Series only) */}
      <RetentionQuizToggleCard />

      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white">Quick actions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button
            onClick={() => navigate('/inbox')}
            variant="outline"
            className="w-full justify-start border-slate-600 text-white hover:bg-slate-700"
            data-testid="profile-open-inbox-btn"
          >
            <Mail className="w-4 h-4 mr-2" /> Open Inbox
          </Button>
          <Button
            onClick={() => navigate('/dashboard')}
            variant="outline"
            className="w-full justify-start border-slate-600 text-white hover:bg-slate-700"
          >
            <Calendar className="w-4 h-4 mr-2" /> View Reading History
          </Button>
          <div className="rounded-lg border border-slate-700 bg-slate-900/30 px-3 py-3 text-sm text-slate-400" data-testid="profile-export-unavailable">
            Data export is not available from this account page yet.
          </div>
        </CardContent>
      </Card>
    </ProfileSubPage>
  );
};

export default ProfileAccount;
